import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatGBP } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { Check, X } from "lucide-react";

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [status, setStatus] = useState("checking"); // checking | paid | failed | expired | pending
  const [order, setOrder] = useState(null);
  const polledRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    if (polledRef.current) return;
    polledRef.current = true;

    let attempts = 0;
    const maxAttempts = 8;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setOrder(data.order);
        if (data.payment_status === "paid") {
          setStatus("paid");
          clear();
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts >= maxAttempts) {
          setStatus("pending");
          return;
        }
        setTimeout(poll, 2200);
      } catch {
        if (attempts >= maxAttempts) setStatus("failed");
        else setTimeout(poll, 2200);
      }
    };
    poll();
  }, [sessionId, clear]);

  return (
    <div className="bg-[#FFF4E0] min-h-[70vh] py-24" data-testid="order-success-page">
      <div className="max-w-2xl mx-auto px-6 text-center">
        {status === "checking" && (
          <>
            <div className="overline">Confirming payment</div>
            <h1 className="font-serif-display text-5xl text-[#1A0F11] mt-4">One moment...</h1>
            <p className="text-[#5C4A4D] mt-4">We&apos;re finalising your order with Stripe.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="mx-auto w-16 h-16 rounded-full bg-[#831132] text-[#FFF4E0] flex items-center justify-center"><Check size={28} /></div>
            <div className="overline mt-6">Order confirmed</div>
            <h1 className="font-serif-display text-5xl md:text-6xl text-[#1A0F11] mt-4">Crown<br /><em className="italic text-[#831132]">received.</em></h1>
            <p className="text-[#5C4A4D] mt-4">Thank you for joining the Hair Bu&apos;tik house. We&apos;ll email tracking details shortly.</p>

            {order && (
              <div className="mt-12 text-left bg-[#FDF1EC] p-8">
                <div className="flex items-center justify-between text-sm">
                  <div>
                    <div className="overline">Order</div>
                    <div className="font-serif-display text-xl text-[#1A0F11] mt-1" data-testid="order-id">{order.order_id}</div>
                  </div>
                  <div className="text-right">
                    <div className="overline">Total</div>
                    <div className="font-serif-display text-xl text-[#1A0F11] mt-1">{formatGBP(order.total)}</div>
                  </div>
                </div>
                <div className="border-t border-[#E8D8C8] mt-6 pt-6 space-y-3">
                  {order.items.map((it, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <div className="text-[#1A0F11]">{it.name} <span className="text-[#5C4A4D]">× {it.quantity}</span></div>
                      <div>{formatGBP(it.price * it.quantity)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-10 flex items-center justify-center gap-4">
              <Link to="/shop" className="btn-outline" data-testid="success-shop-more">Continue shopping</Link>
              <Link to="/account" className="btn-primary" data-testid="success-view-account">My orders</Link>
            </div>
          </>
        )}
        {(status === "failed" || status === "expired") && (
          <>
            <div className="mx-auto w-16 h-16 rounded-full bg-[#1A0F11] text-[#FFF4E0] flex items-center justify-center"><X size={28} /></div>
            <h1 className="font-serif-display text-5xl text-[#1A0F11] mt-6">Payment didn&apos;t complete.</h1>
            <p className="text-[#5C4A4D] mt-4">Don&apos;t worry — your bag is saved. Try again whenever you&apos;re ready.</p>
            <Link to="/cart" className="btn-primary mt-8 inline-flex">Return to cart</Link>
          </>
        )}
        {status === "pending" && (
          <>
            <h1 className="font-serif-display text-5xl text-[#1A0F11]">Still processing.</h1>
            <p className="text-[#5C4A4D] mt-4">We&apos;ll email you the moment your payment confirms.</p>
            <Link to="/shop" className="btn-outline mt-8 inline-flex">Continue shopping</Link>
          </>
        )}
      </div>
    </div>
  );
}
